import * as ImageManipulator from 'expo-image-manipulator';
import * as VideoThumbnails from 'expo-video-thumbnails';

/**
 * Extract frames from a video at regular intervals across its actual duration.
 *
 * Frames are sampled evenly: frame 0 at 0ms, frame N-1 at (durationMs - 1ms)
 * with the rest spaced linearly in between. If durationMs is omitted we fall
 * back to a 15s window (the legacy behaviour), but callers should always
 * pass it when available so short videos don't oversample the start and
 * long videos don't miss the end.
 *
 * 10 frames matches MAX_PRODUCTS_PER_VIDEO on the backend — gives Gemini
 * enough samples that each detected product is likely to appear in at least
 * 2 frames, while keeping upload size and Gemini input tokens bounded.
 *
 * @param videoUri - URI of the video file
 * @param frameCount - Number of frames to extract (default: 10)
 * @param durationMs - Actual video duration in milliseconds (from ImagePicker asset)
 * @returns Array of base64-encoded JPEG images
 */
export async function extractVideoFrames(
  videoUri: string,
  frameCount: number = 10,
  durationMs?: number,
): Promise<string[]> {
  try {
    const totalMs = durationMs && durationMs > 0 ? durationMs : 15000;
    console.log(
      `[VIDEO] Extracting ${frameCount} frames across ${totalMs}ms from HD video: ${videoUri}`,
    );

    const frames: string[] = [];

    // Space frames evenly across [0, totalMs). 1ms safety margin on the tail
    // because getThumbnailAsync can fail at exactly duration on some platforms.
    const interval = totalMs / Math.max(1, frameCount - 1);

    for (let i = 0; i < frameCount; i++) {
      const timeMs = Math.floor(Math.min(i * interval, totalMs - 1));

      console.log(`[VIDEO] Extracting frame ${i + 1}/${frameCount} at ${timeMs}ms`);
      
      try {
        // Generate high-quality thumbnail at specific time
        // quality: 1.0 for maximum quality from HD source
        const { uri } = await VideoThumbnails.getThumbnailAsync(videoUri, {
          time: timeMs,
          quality: 1.0, // Maximum quality to preserve HD details
        });
        
        console.log(`[VIDEO] Frame ${i + 1} extracted: ${uri}`);
        
        // Resize to 512px width for Gemini (as per memory requirements)
        // Use minimal compression to preserve quality from HD source
        const manipResult = await ImageManipulator.manipulateAsync(
          uri,
          [{ resize: { width: 512 } }],
          {
            compress: 1.0, // No compression - preserve maximum quality
            format: ImageManipulator.SaveFormat.JPEG,
            base64: true,
          }
        );
        
        if (manipResult.base64) {
          frames.push(manipResult.base64);
          console.log(`[VIDEO] Frame ${i + 1} processed (base64 length: ${manipResult.base64.length})`);
        }
      } catch (frameError) {
        console.warn(`[VIDEO] Failed to extract frame ${i + 1}:`, frameError);
        // Continue with other frames
      }
    }
    
    if (frames.length === 0) {
      throw new Error('Failed to extract any frames from the video');
    }
    
    console.log(`[VIDEO] Successfully extracted ${frames.length} high-quality frames from HD source`);
    return frames;
  } catch (error) {
    console.error('[VIDEO] Error extracting video frames:', error);
    throw error;
  }
}

/**
 * Get video duration in seconds
 * @param videoUri - URI of the video file
 * @returns Duration in seconds
 */
export async function getVideoDuration(videoUri: string): Promise<number> {
  try {
    // This would require expo-av Video component
    // Placeholder for now
    return 0;
  } catch (error) {
    console.error('Error getting video duration:', error);
    return 0;
  }
}
